
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const location = useLocation();
  const isMobile = useIsMobile();
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const resourcesRef = useRef<HTMLDivElement>(null);
  const resourcesBtnRef = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isSpecialPage = location.pathname === '/testimonials' || location.pathname === '/community' || location.pathname === '/customer-stories' || location.pathname === '/case-studies' || location.pathname.startsWith('/case-studies/') || location.pathname === '/blogs' || location.pathname.startsWith('/blogs/');

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setResourcesOpen(false);
  }, [location.pathname]);

  // Close mobile dropdown when tapping outside
  useEffect(() => {
    if (!isMobile || !resourcesOpen) return;
    const handleTouchOutside = (e: TouchEvent | MouseEvent) => {
      if (resourcesBtnRef.current?.contains(e.target as Node)) return;
      if (mobileDropdownRef.current?.contains(e.target as Node)) return;
      setResourcesOpen(false);
    };
    document.addEventListener('touchstart', handleTouchOutside);
    document.addEventListener('mousedown', handleTouchOutside);
    return () => {
      document.removeEventListener('touchstart', handleTouchOutside);
      document.removeEventListener('mousedown', handleTouchOutside);
    };
  }, [isMobile, resourcesOpen]);

  const handleResourcesEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setResourcesOpen(true);
  };

  const handleResourcesLeave = () => {
    timeoutRef.current = setTimeout(() => setResourcesOpen(false), 200);
  };

  const handleResourcesClick = () => {
    if (isMobile && resourcesBtnRef.current) {
      const rect = resourcesBtnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setResourcesOpen(!resourcesOpen);
  };

  const isActive = (path: string) => location.pathname === path;
  const isResourcesActive = location.pathname === '/blogs' || location.pathname === '/case-studies' || location.pathname.startsWith('/case-studies/') || location.pathname === '/customer-stories';

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Domains', path: '/domains' },
    { name: 'KadenceTalent', path: '/kadence-talent' },
    { name: 'KadenceSearch', path: '/kadence-search' },
    { name: 'KadencePHD', path: '/kadence-phd' },
    { name: 'Opportunities', path: '/opportunities' },
  ];

  const navLinksAfter = [
    { name: 'Community', path: '/community' },
    { name: 'Contact', path: '/contact' },
  ];

  const resourcesDropdownItems = [
    { name: 'Blogs', path: '/blogs' },
    { name: 'Case Studies', path: '/case-studies' },
    { name: 'Customer Stories', path: '/customer-stories' },
  ];

  const linkClass = (path: string) =>
    `transition-colors duration-200 px-3 py-2 text-sm font-medium 
    ${isActive(path) ? 'text-kadence-pink' : 'text-white hover:text-kadence-pink'}
    relative after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-kadence-pink 
    after:transition-all after:duration-300 
    ${isActive(path) ? 'after:w-full' : 'after:w-0 hover:after:w-full'}`;

  const dropdownMenu = (
    <div className="w-48 bg-kadence-darknavy border border-white/10 rounded-lg shadow-xl overflow-hidden">
      {resourcesDropdownItems.map((item) => (
        <Link
          key={item.name}
          to={item.path}
          className={`block px-4 py-3 text-sm transition-colors duration-150
            ${isActive(item.path) ? 'text-kadence-pink bg-white/5' : 'text-white hover:text-kadence-pink hover:bg-white/5'}`}
        >
          {item.name}
        </Link>
      ))}
    </div>
  );

  const renderResourcesDropdown = () => (
    <div
      ref={resourcesRef}
      className="relative"
      onMouseEnter={!isMobile ? handleResourcesEnter : undefined}
      onMouseLeave={!isMobile ? handleResourcesLeave : undefined}
    >
      <button
        ref={resourcesBtnRef}
        className={`transition-colors duration-200 px-3 py-2 text-sm font-medium inline-flex items-center gap-1
          ${isResourcesActive ? 'text-kadence-pink' : 'text-white hover:text-kadence-pink'}
          relative after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-kadence-pink 
          after:transition-all after:duration-300 
          ${isResourcesActive ? 'after:w-full' : 'after:w-0 hover:after:w-full'}`}
        onClick={handleResourcesClick}
        aria-expanded={resourcesOpen}
        aria-haspopup="true"
      >
        Resources
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`} />
      </button>
      {/* Desktop: render inline */}
      {resourcesOpen && !isMobile && (
        <div className="absolute top-full left-0 mt-2 z-[9999]">
          {dropdownMenu}
        </div>
      )}
      {/* Mobile: render via portal to escape overflow clipping */}
      {resourcesOpen && isMobile && dropdownPos && createPortal(
        <div
          ref={mobileDropdownRef}
          className="fixed z-[9999]"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          {dropdownMenu}
        </div>,
        document.body
      )}
    </div>
  );

  return (
    <nav 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isSpecialPage 
          ? 'bg-kadence-darknavy py-4 shadow-lg' 
          : isScrolled 
            ? 'bg-kadence-darknavy/95 backdrop-blur-md py-4 shadow-lg' 
            : 'bg-transparent py-6'
      }`}
    >
      {!isMobile ? (
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-center">
          <div className="flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link key={link.name} to={link.path} className={linkClass(link.path)}>
                {link.name}
              </Link>
            ))}
            {renderResourcesDropdown()}
            {navLinksAfter.map((link) => (
              <Link key={link.name} to={link.path} className={linkClass(link.path)}>
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="container mx-auto px-4">
          <div 
            ref={scrollRef}
            className="overflow-x-auto scrollbar-hide pb-1" 
            style={{ 
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <div className="flex items-center space-x-5 py-2 min-w-max">
              {navLinks.map((link) => (
                <Link key={link.name} to={link.path} className={`${linkClass(link.path)} flex-shrink-0`}>
                  {link.name}
                </Link>
              ))}
              {renderResourcesDropdown()}
              {navLinksAfter.map((link) => (
                <Link key={link.name} to={link.path} className={`${linkClass(link.path)} flex-shrink-0`}>
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
